// packages/ui/src/pages/WebChat.tsx
// Full chat page with message list, input, WS streaming, and auto-scroll

import { useState, useEffect, useRef, useCallback } from 'react'
import { MessageBubble, type ChatMessage } from '../components/chat/MessageBubble'
import { ChatInput } from '../components/chat/ChatInput'
import { apiRequest } from '../api/client'

interface ChatSendResponse {
  messageId: string
  content: string
  toolResults?: Array<{
    tool: string
    params?: Record<string, unknown>
    result?: unknown
    error?: string
  }>
}

let msgCounter = 0
function nextId(): string {
  return `msg-${++msgCounter}-${Date.now()}`
}

export function WebChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)
  const wsRef = useRef<WebSocket | null>(null)

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [messages])

  // WebSocket connection for streaming responses
  useEffect(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${protocol}//${window.location.host}/api/dashboard/ws`

    try {
      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onmessage = (msg) => {
        try {
          const parsed = JSON.parse(msg.data)

          if (parsed.type === 'chat:chunk') {
            // Streaming text chunk — append to last assistant message
            setMessages((prev) => {
              const last = prev[prev.length - 1]
              if (last?.role === 'assistant' && last.isStreaming) {
                return [
                  ...prev.slice(0, -1),
                  { ...last, content: last.content + (parsed.payload?.text ?? '') },
                ]
              }
              return prev
            })
          } else if (parsed.type === 'chat:done') {
            // Streaming complete
            setMessages((prev) => {
              const last = prev[prev.length - 1]
              if (last?.role === 'assistant' && last.isStreaming) {
                return [
                  ...prev.slice(0, -1),
                  {
                    ...last,
                    isStreaming: false,
                    toolResults: parsed.payload?.toolResults ?? last.toolResults,
                  },
                ]
              }
              return prev
            })
            setIsStreaming(false)
          } else if (parsed.type === 'chat:tool') {
            // Tool result during streaming
            setMessages((prev) => {
              const last = prev[prev.length - 1]
              if (last?.role === 'assistant' && last.isStreaming) {
                const toolResults = [...(last.toolResults ?? []), parsed.payload]
                return [
                  ...prev.slice(0, -1),
                  { ...last, toolResults },
                ]
              }
              return prev
            })
          }
        } catch {
          // Ignore parse errors
        }
      }

      ws.onerror = () => {
        // Silently handle WS errors
      }
    } catch {
      // WebSocket not available
    }

    return () => {
      wsRef.current?.close()
    }
  }, [])

  const handleSend = useCallback(async (text: string) => {
    // Add user message
    const userMsg: ChatMessage = {
      id: nextId(),
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    }

    // Add placeholder assistant message for streaming
    const assistantMsg: ChatMessage = {
      id: nextId(),
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true,
    }

    setMessages((prev) => [...prev, userMsg, assistantMsg])
    setIsStreaming(true)

    try {
      const response = await apiRequest<ChatSendResponse>('/chat/send', {
        method: 'POST',
        body: JSON.stringify({ message: text }),
      })

      // If the API returns a full response (non-streaming fallback),
      // update the assistant message
      setMessages((prev) => {
        const last = prev[prev.length - 1]
        if (last?.role === 'assistant' && last.isStreaming) {
          // Only update if still streaming (WS may have already handled it)
          if (!last.content && response.content) {
            return [
              ...prev.slice(0, -1),
              {
                ...last,
                content: response.content,
                isStreaming: false,
                toolResults: response.toolResults,
              },
            ]
          }
        }
        return prev
      })
    } catch (err) {
      // Show error as assistant message
      setMessages((prev) => {
        const last = prev[prev.length - 1]
        if (last?.role === 'assistant' && last.isStreaming) {
          return [
            ...prev.slice(0, -1),
            {
              ...last,
              content: `Error: ${(err as Error).message}`,
              isStreaming: false,
            },
          ]
        }
        return prev
      })
    } finally {
      setIsStreaming(false)
    }
  }, [])

  return (
    <div data-testid="chat-page" className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
        <h1 className="text-2xl font-bold">Chat</h1>
        <span className="text-xs text-gray-500" data-testid="message-count">
          {messages.length} messages
        </span>
      </div>

      {/* Message list */}
      <div
        ref={listRef}
        data-testid="message-list"
        className="flex-1 overflow-y-auto px-4 py-4"
      >
        {messages.length === 0 ? (
          <div data-testid="chat-empty" className="flex flex-col items-center justify-center h-full text-gray-500">
            <p className="text-lg mb-1">No messages yet</p>
            <p className="text-sm">Start a conversation with the Droid agent.</p>
          </div>
        ) : (
          messages.map((msg) => (
            <MessageBubble key={msg.id} message={msg} />
          ))
        )}
      </div>

      {/* Input */}
      <ChatInput
        onSend={handleSend}
        disabled={isStreaming}
        placeholder={isStreaming ? 'Waiting for response...' : 'Type a message...'}
      />
    </div>
  )
}
